import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Sparkle, ArrowRight, Lock, Check } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";
import { resetPassword, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const { lang } = useI18n();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError(lang === "el" ? "Τουλάχιστον 6 χαρακτήρες" : "At least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError(lang === "el" ? "Οι κωδικοί δεν ταιριάζουν" : "Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      toast.success(lang === "el" ? "Ο κωδικός ενημερώθηκε" : "Password updated");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-6 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 kicker-label mb-4">
            <Sparkle size={14} weight="duotone" className="text-forest" />
            {lang === "el" ? "Νέος κωδικός" : "New password"}
          </div>
          <h1 className="font-heading font-semibold text-[36px] md:text-[44px] leading-[0.95] tracking-tight text-ink">
            {done
              ? (lang === "el" ? "Έτοιμα!" : "All set!")
              : (lang === "el" ? "Όρισε νέο κωδικό." : "Set a new password.")}
          </h1>
          <p className="mt-3 text-ink-soft text-sm">
            {done
              ? (lang === "el" ? "Σε μεταφέρουμε στη σύνδεση…" : "Redirecting to sign in…")
              : (lang === "el" ? "Επίλεξε έναν νέο ασφαλή κωδικό." : "Choose a new secure password.")}
          </p>
        </div>

        {!done ? (
          <form onSubmit={submit} className="space-y-4 bg-white border border-line rounded-2xl canvas-shadow p-6" data-testid="reset-form">
            <div>
              <label className="kicker-label mb-2 block">{lang === "el" ? "Νέος κωδικός" : "New password"}</label>
              <div className="relative">
                <Lock size={16} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="••••••••"
                  className="cs-input !pl-11"
                  data-testid="reset-password"
                  autoFocus
                />
              </div>
            </div>
            <div>
              <label className="kicker-label mb-2 block">{lang === "el" ? "Επιβεβαίωση" : "Confirm"}</label>
              <div className="relative">
                <Lock size={16} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="cs-input !pl-11"
                  data-testid="reset-confirm"
                />
              </div>
            </div>

            {error && (
              <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2" data-testid="reset-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream rounded-full px-6 py-3.5 text-sm font-medium hover:bg-forest-dark transition disabled:opacity-60"
              data-testid="reset-submit"
            >
              {loading ? "…" : (lang === "el" ? "Αλλαγή κωδικού" : "Update password")}
              <ArrowRight size={14} weight="bold" />
            </button>

            <div className="text-center text-sm text-ink-soft pt-2">
              <Link to="/login" className="text-forest hover:text-forest-dark font-medium">
                ← {lang === "el" ? "Πίσω στη σύνδεση" : "Back to sign in"}
              </Link>
            </div>
          </form>
        ) : (
          <div className="bg-white border border-line rounded-2xl canvas-shadow p-8 text-center" data-testid="reset-done">
            <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-4">
              <Check size={20} weight="bold" className="text-forest" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
